import { useState, useMemo } from "react";
import { useGetMemberAppointments, useUpdateAppointmentStatus, useListMemberEstimates, API_BASE } from "@/lib/api-client-react";
import MemberLayout from "@/layouts/MemberLayout";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ListPagination } from "@/components/ui/list-pagination";
import {
  Search, ChevronLeft, ChevronRight, Calendar as CalendarIcon,
  Eye, X, User, Package, Stethoscope, DollarSign, Tag,
  FileText, Hash, Phone, Mail, MapPin, Clock, Globe, Building2, Key, History
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// ─── Helpers ──────────────────────────────────────────────────────────────────
const getStatusColor = (status: string) => {
  const s = String(status || '').toUpperCase();
  switch (s) {
    case 'PENDING':   return 'bg-amber-100 dark:bg-amber-950/50 text-amber-800 dark:text-amber-300 border-amber-200 dark:border-amber-800/60';
    case 'APPROVED':  
    case 'ACCEPTED':  
    case 'CONFIRMED': return 'bg-emerald-100 dark:bg-emerald-950/50 text-emerald-800 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800/60';
    case 'PAID':
    case 'SCHEDULED': return 'bg-blue-100 dark:bg-blue-950/50 text-blue-800 dark:text-blue-300 border-blue-200 dark:border-blue-800/60';
    case 'REJECTED':
    case 'CANCELLED': 
    case 'REFUNDED':  return 'bg-red-100 dark:bg-red-950/50 text-red-800 dark:text-red-300 border-red-200 dark:border-red-800/60';
    case 'COMPLETED': return 'bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-200 border-slate-200 dark:border-slate-700';
    default:          return 'bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-200 border-slate-200 dark:border-slate-700';
  }
};

const getStatusLabel = (status: string) => {
  const s = String(status || '').toUpperCase();
  switch (s) {
    case 'PENDING':   return 'PENDING REVIEW';
    case 'APPROVED':  
    case 'ACCEPTED':  
    case 'CONFIRMED': return 'CONFIRMED & BOOKED';
    case 'PAID':
    case 'SCHEDULED': return 'PAID & SCHEDULED';
    case 'COMPLETED': return 'COMPLETED VISIT';
    case 'CANCELLED': return 'CANCELLED';
    case 'REJECTED':  return 'REJECTED';
    case 'REFUNDED':  return 'REFUNDED';
    default:          return status;
  }
};

const DUMMY_APPOINTMENTS = [
  {
    id: "apt-101",
    status: "CONFIRMED",
    amount: 680,
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2).toISOString(),
    verificationCode: "VCH-77492",
    notes: "Patient requested morning appointment slot between 9:00 AM and 11:00 AM.",
    provider: {
      userId: "usr-prov-101",
      facilityName: "Metro Cardiology Center",
      user: {
        id: "usr-prov-101",
        displayName: "Dr. Sarah Mitchell, MD",
        firstName: "Sarah",
        lastName: "Mitchell",
        email: "dr.mitchell@metrocardio.org",
        phone: "(555) 234-5678",
        specialty: "Cardiology",
        addresses: [
          {
            buildingNo: "Suite 400",
            street1: "100 Medical Center Blvd",
            city: "New York",
            state: "NY",
            zipCode: "10001",
            phone: "(555) 234-5678",
            email: "appointments@metrocardio.org"
          }
        ]
      }
    },
    bundle: {
      id: "bnd-101",
      name: "Comprehensive Cardio & Wellness Package",
      description: "Complete cardiovascular evaluation including resting ECG, comprehensive metabolic panel, echocardiogram, and physician consultation.",
      category: "Cardiology",
      status: "active",
      totalPrice: 850,
      finalPrice: 680,
      discountPercent: 20,
      cptCodes: [
        { id: "cpt-1", code: "99204", description: "Comprehensive New Patient Office Visit", price: 220 },
        { id: "cpt-2", code: "93000", description: "Electrocardiogram (ECG/EKG) 12-lead", price: 150 },
        { id: "cpt-3", code: "93306", description: "Transthoracic Echocardiography Complete", price: 480 }
      ]
    }
  },
  {
    id: "apt-102",
    status: "PENDING",
    amount: 960,
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 12).toISOString(),
    verificationCode: "VCH-39184",
    notes: "Patient requested consultation for recurring knee discomfort and mobility rehabilitation.",
    provider: {
      userId: "usr-prov-102",
      facilityName: "Vance Orthopedic Center",
      user: {
        id: "usr-prov-102",
        displayName: "Dr. Robert Vance, MD",
        firstName: "Robert",
        lastName: "Vance",
        email: "contact@vanceortho.com",
        phone: "(555) 876-5432",
        specialty: "Orthopedics",
        addresses: [
          {
            buildingNo: "Plaza West",
            street1: "450 Orthopedic Way",
            city: "Chicago",
            state: "IL",
            zipCode: "60611",
            phone: "(555) 876-5432",
            email: "care@vanceortho.com"
          }
        ]
      }
    },
    bundle: {
      id: "bnd-102",
      name: "Orthopedic Knee Joint & Rehab Package",
      description: "Targeted knee joint examination, digital bilateral X-rays, guided corticosteroid therapy, and initial physical therapy session.",
      category: "Orthopedics",
      status: "active",
      totalPrice: 1200,
      finalPrice: 960,
      discountPercent: 20,
      cptCodes: [
        { id: "cpt-4", code: "99214", description: "Established Patient Clinical Evaluation", price: 175 },
        { id: "cpt-5", code: "73562", description: "Digital X-Ray Knee 3 Views", price: 225 },
        { id: "cpt-6", code: "20610", description: "Arthrocentesis / Major Joint Injection", price: 380 },
        { id: "cpt-7", code: "97110", description: "Physical Therapy Therapeutic Exercise", price: 420 }
      ]
    }
  },
  {
    id: "apt-103",
    status: "SCHEDULED",
    amount: 440,
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 36).toISOString(),
    verificationCode: "VCH-55102",
    notes: "Annual preventative wellness exam. Fasting required 8 hours prior to lab draw.",
    provider: {
      userId: "usr-prov-103",
      facilityName: "Horizon Primary Care Clinic",
      user: {
        id: "usr-prov-103",
        displayName: "Dr. Emily Chen, MD",
        firstName: "Emily",
        lastName: "Chen",
        email: "reception@horizonprimary.org",
        phone: "(555) 345-6789",
        specialty: "Preventative Care",
        addresses: [
          {
            buildingNo: "Building B, 2nd Floor",
            street1: "780 Health Parkway",
            city: "Austin",
            state: "TX",
            zipCode: "78701",
            phone: "(555) 345-6789",
            email: "support@horizonprimary.org"
          }
        ]
      }
    },
    bundle: {
      id: "bnd-103",
      name: "Executive Health & Preventative Screening",
      description: "Full body wellness examination with comprehensive lipid panel, biometric screening, lung function test, and lifestyle consult.",
      category: "Preventative Care",
      status: "active",
      totalPrice: 550,
      finalPrice: 440,
      discountPercent: 20,
      cptCodes: [
        { id: "cpt-8", code: "99385", description: "Initial Preventative Physical Exam (18-39)", price: 250 },
        { id: "cpt-9", code: "80061", description: "Lipid Panel (Cholesterol & Triglycerides)", price: 95 },
        { id: "cpt-10", code: "80053", description: "Comprehensive Metabolic Panel (CMP)", price: 110 },
        { id: "cpt-11", code: "94010", description: "Spirometry Pulmonary Function Assessment", price: 95 }
      ]
    }
  },
  {
    id: "apt-104",
    status: "COMPLETED",
    amount: 1150,
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 14).toISOString(),
    verificationCode: "VCH-98120",
    notes: "Diagnostic MRI completed successfully. Results and imaging scans uploaded to portal.",
    provider: {
      userId: "usr-prov-104",
      facilityName: "Apex Imaging Diagnostics",
      user: {
        id: "usr-prov-104",
        displayName: "Dr. David Rodriguez, MD",
        firstName: "David",
        lastName: "Rodriguez",
        email: "info@apeximaging.com",
        phone: "(555) 456-7890",
        specialty: "Radiology & Imaging",
        addresses: [
          {
            buildingNo: "Diagnostics Suite 10",
            street1: "210 Imaging Center Dr",
            city: "San Francisco",
            state: "CA",
            zipCode: "94107",
            phone: "(555) 456-7890",
            email: "records@apeximaging.com"
          }
        ]
      }
    },
    bundle: {
      id: "bnd-104",
      name: "High-Resolution Spine MRI Diagnostic Suite",
      description: "Lumbar spine MRI with and without contrast, professional radiologist interpretation, and digital imaging portal access.",
      category: "Radiology & Imaging",
      status: "active",
      totalPrice: 1650,
      finalPrice: 1150,
      discountPercent: 30,
      cptCodes: [
        { id: "cpt-12", code: "72148", description: "MRI Lumbar Spine without Contrast", price: 850 },
        { id: "cpt-13", code: "72149", description: "MRI Lumbar Spine with Contrast", price: 800 }
      ]
    }
  },
  {
    id: "apt-105",
    status: "CANCELLED",
    amount: 480,
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 20).toISOString(),
    verificationCode: "VCH-66231",
    notes: "Appointment rescheduled by patient. Refund voucher issued.",
    provider: {
      userId: "usr-prov-105",
      facilityName: "Thorne Dental Studio",
      user: {
        id: "usr-prov-105",
        displayName: "Dr. Marcus Thorne, DDS",
        firstName: "Marcus",
        lastName: "Thorne",
        email: "care@thornedental.com",
        phone: "(555) 567-8901",
        specialty: "Dental Care",
        addresses: [
          {
            buildingNo: "Level 1",
            street1: "330 Smile Blvd",
            city: "Seattle",
            state: "WA",
            zipCode: "98101",
            phone: "(555) 567-8901",
            email: "office@thornedental.com"
          }
        ]
      }
    },
    bundle: {
      id: "bnd-105",
      name: "Complete Dental Restoration & Deep Cleaning",
      description: "Comprehensive periodontal evaluation, ultrasonic full-mouth scaling, fluoride varnish treatment, and digital panoramic X-ray.",
      category: "Dental Care",
      status: "active",
      totalPrice: 640,
      finalPrice: 480,
      discountPercent: 25,
      cptCodes: [
        { id: "cpt-14", code: "D0150", description: "Comprehensive Oral Dental Evaluation", price: 110 },
        { id: "cpt-15", code: "D0330", description: "Panoramic Dental Radiographic Image", price: 140 },
        { id: "cpt-16", code: "D4341", description: "Periodontal Scaling & Root Planing", price: 390 }
      ]
    }
  }
];

function SectionCard({ title, icon: Icon, children }: { title: string; icon: any; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30 overflow-hidden">
      <div className="flex items-center gap-2 px-5 py-3 bg-white dark:bg-slate-800/60 border-b border-slate-100 dark:border-slate-700">
        <Icon className="w-4 h-4 text-primary" />
        <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">{title}</h3>
      </div>
      <div className="px-5 py-4 space-y-3">{children}</div>
    </div>
  );
}

function InfoRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3">
      <span className="text-slate-400 mt-0.5 flex-shrink-0">{icon}</span>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-slate-400 font-medium mb-0.5">{label}</p>
        <div className="text-sm text-slate-700 dark:text-slate-200 font-medium break-words">{value}</div>
      </div>
    </div>
  );
}

// ─── Detail Modal ─────────────────────────────────────────────────────────────
function AppointmentDetailModal({ apt, onClose, onCancel, isCancelling }: {
  apt: any;
  onClose: () => void;
  onCancel: (id: string) => void;
  isCancelling: boolean;
}) {
  const [activeTab, setActiveTab] = useState<"appointment" | "provider" | "bundle" | "estimate">("appointment");

  const provider = apt.provider;
  const pu = apt.groupUser || provider?.user;
  const bundle = apt.bundle;

  const { data: estimates } = useListMemberEstimates();
  const estimateId = apt.notes?.match(/\[ESTIMATE:[^:]+:([^\]]+)\]/)?.[1];
  const estimate = estimateId ? estimates?.find((e: any) => e.id === estimateId) : null;

  const tabs = [
    { key: "appointment" as const, label: "Appointment", icon: CalendarIcon },
    { key: "provider"    as const, label: "Provider",    icon: Stethoscope  },
    ...(bundle ? [{ key: "bundle" as const, label: "Bundle", icon: Package }] : []),
    ...(estimate ? [{ key: "estimate" as const, label: "Package", icon: Package }] : []),
  ];

  const providerName = apt.groupName || pu?.displayName || `${pu?.firstName || ''} ${pu?.lastName || ''}`.trim() || 'Provider';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
    >
      <div
        className="relative bg-white dark:bg-slate-900 rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between px-7 pt-7 pb-5 border-b border-slate-100 dark:border-slate-800 bg-gradient-to-r from-primary/5 to-indigo-500/5">
          <div className="flex items-center gap-4">
            {pu?.profilePicture ? (
              <img src={`${API_BASE.replace(/\/v1|\/api/, '')}${pu.profilePicture}`} alt={providerName} className="w-14 h-14 rounded-2xl object-cover flex-shrink-0 border border-slate-200 dark:border-slate-700" />
            ) : (
              <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <span className="text-2xl font-bold text-primary">
                  {providerName.charAt(0).toUpperCase()}
                </span>
              </div>
            )}
            <div>
              <h2 className="text-xl font-bold text-slate-900 dark:text-white">{providerName}</h2>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant="outline" className={`text-xs px-2 py-0.5 rounded-full font-semibold ${getStatusColor(apt.status)}`}>
                  {apt.status}
                </Badge>
                <span className="text-xs text-slate-400 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {format(new Date(apt.createdAt), 'MMM d, yyyy · h:mm a')}
                </span>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 flex items-center justify-center transition-colors flex-shrink-0 mt-1"
          >
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-100 dark:border-slate-800 px-7 gap-1 pt-1">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors -mb-px ${
                activeTab === tab.key
                  ? 'border-primary text-primary'
                  : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-7 py-6 space-y-5">

          {/* ── Appointment Tab ── */}
          {activeTab === 'appointment' && (
            <div className="space-y-5">
              <SectionCard title="Appointment Details" icon={CalendarIcon}>
                <InfoRow icon={<Hash className="w-4 h-4" />} label="Appointment ID" value={<span className="font-mono text-xs text-slate-500">{apt.id}</span>} />
                <InfoRow icon={<Clock className="w-4 h-4" />} label="Requested On" value={format(new Date(apt.createdAt), 'MMMM d, yyyy · h:mm a')} />
                <InfoRow icon={<Tag className="w-4 h-4" />} label="Status" value={
                  <Badge variant="outline" className={`text-xs px-2 py-0.5 rounded-full font-semibold ${getStatusColor(apt.status)}`}>
                    {apt.status}
                  </Badge>
                } />
                <InfoRow icon={<DollarSign className="w-4 h-4" />} label="Estimated Amount" value={
                  <span className="text-lg font-bold text-slate-900 dark:text-white">${Number(apt.amount || 0).toLocaleString()}</span>
                } />
                {apt.verificationCode && apt.status !== 'COMPLETED' && apt.status !== 'CANCELLED' && apt.status !== 'REJECTED' && (
                  <InfoRow
                    icon={<Key className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />}
                    label="Verification Code"
                    value={
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-mono text-base font-bold bg-indigo-50 dark:bg-indigo-950 text-indigo-700 dark:text-indigo-300 px-3 py-1 rounded-lg border border-indigo-200 dark:border-indigo-800 tracking-wider">
                          {apt.verificationCode}
                        </span>
                        <span className="text-xs text-slate-500 dark:text-slate-400 font-normal">Share this code with your provider upon visit</span>
                      </div>
                    }
                  />
                )}
                {bundle && <InfoRow icon={<Package className="w-4 h-4" />} label="Bundle" value={bundle.name} />}
              </SectionCard>

              {apt.notes && apt.notes.replace(/\[ESTIMATE:[^\]]+\]/g, '').trim() && (
                <SectionCard title="Notes" icon={FileText}>
                  <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">{apt.notes.replace(/\[ESTIMATE:[^\]]+\]/g, '').trim()}</p>
                </SectionCard>
              )}


            </div>
          )}

          {/* ── Provider Tab ── */}
          {activeTab === 'provider' && (
            <div className="space-y-5">
              <SectionCard title="Provider Information" icon={User}>
                <InfoRow icon={<User className="w-4 h-4" />} label="Full Name" value={providerName} />
                {pu?.email && <InfoRow icon={<Mail className="w-4 h-4" />} label="Email" value={<a href={`mailto:${pu.email}`} className="text-primary hover:underline">{pu.email}</a>} />}
                {pu?.phone && <InfoRow icon={<Phone className="w-4 h-4" />} label="Phone" value={<a href={`tel:${pu.phone}`} className="text-primary hover:underline">{pu.phone}</a>} />}
                {!pu?.email && !pu?.phone && (
                  <p className="text-sm text-slate-400 italic">No contact details available</p>
                )}
              </SectionCard>

              {/* Location */}
              {pu?.addresses && pu.addresses.length > 0 && (() => {
                const addr = pu.addresses[0];
                const addrStr = [addr.buildingNo, addr.street1, addr.street2, addr.city, addr.state, addr.zipCode].filter(Boolean).join(', ');
                return addrStr ? (
                  <SectionCard title="Location" icon={MapPin}>
                    {addr.buildingNo && <InfoRow icon={<Building2 className="w-4 h-4" />} label="Building" value={addr.buildingNo} />}
                    {(addr.street1 || addr.street2) && <InfoRow icon={<MapPin className="w-4 h-4" />} label="Street" value={[addr.street1, addr.street2].filter(Boolean).join(', ')} />}
                    {addr.city && <InfoRow icon={<Globe className="w-4 h-4" />} label="City / State" value={[addr.city, addr.state].filter(Boolean).join(', ')} />}
                    {addr.zipCode && <InfoRow icon={<Hash className="w-4 h-4" />} label="ZIP Code" value={addr.zipCode} />}
                    {addr.phone && <InfoRow icon={<Phone className="w-4 h-4" />} label="Office Phone" value={addr.phone} />}
                    {addr.email && <InfoRow icon={<Mail className="w-4 h-4" />} label="Office Email" value={addr.email} />}
                  </SectionCard>
                ) : null;
              })()}

              {/* Assigned Provider (shown when this is a group appointment) */}
              {apt.groupName && apt.provider?.user && (
                <SectionCard title="Assigned Provider" icon={Stethoscope}>
                  <InfoRow icon={<User className="w-4 h-4" />} label="Full Name" value={
                    <span className="text-indigo-600 dark:text-indigo-400 font-semibold">
                      {apt.provider.user.displayName || `${apt.provider.user.firstName || ''} ${apt.provider.user.lastName || ''}`.trim()}
                    </span>
                  } />
                  {apt.provider.user.email && (
                    <InfoRow icon={<Mail className="w-4 h-4" />} label="Email" value={
                      <a href={`mailto:${apt.provider.user.email}`} className="text-primary hover:underline">{apt.provider.user.email}</a>
                    } />
                  )}
                  {apt.provider.user.phone && (
                    <InfoRow icon={<Phone className="w-4 h-4" />} label="Phone" value={apt.provider.user.phone} />
                  )}
                </SectionCard>
              )}

              {/* Provider profile link */}
              <a
                href={`/provider/${(providerName || 'provider').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}/${provider?.userId || pu?.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl border border-primary/30 text-primary text-sm font-medium hover:bg-primary/5 transition-colors"
              >
                <Eye className="w-4 h-4" /> View Full Provider Profile
              </a>
            </div>
          )}

          {/* ── Estimate Tab ── */}
          {activeTab === 'estimate' && estimate && (
            <div className="space-y-5">
              <SectionCard title="Custom Package Overview" icon={Package}>
                <InfoRow icon={<Package className="w-4 h-4" />} label="Name" value="Custom Estimate Package" />
              </SectionCard>

              <SectionCard title="Pricing" icon={DollarSign}>
                <InfoRow icon={<DollarSign className="w-4 h-4" />} label="Total Price" value={`$${Number(estimate.totalCharges || 0).toLocaleString()}`} />
                <InfoRow icon={<Tag className="w-4 h-4" />} label="Discount" value={`${estimate.discountPercent || 0}%`} />
                <div className="mt-2 pt-2 border-t border-slate-100 dark:border-slate-700">
                  <InfoRow icon={<DollarSign className="w-4 h-4 text-emerald-600" />} label="Final Price" value={
                    <span className="text-lg font-bold text-emerald-600">${Number(estimate.finalAmount || 0).toLocaleString()}</span>
                  } />
                </div>
              </SectionCard>

              {estimate.items && estimate.items.length > 0 && (
                <SectionCard title={`CPT Codes (${estimate.items.length})`} icon={Hash}>
                  <div className="overflow-x-auto -mx-1">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-100 dark:border-slate-700">
                          <th className="text-left py-2 px-1 text-xs font-semibold text-slate-500 uppercase tracking-wide">Code</th>
                          <th className="text-left py-2 px-1 text-xs font-semibold text-slate-500 uppercase tracking-wide">Description</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-50 dark:divide-slate-800">
                        {estimate.items.map((cpt: any, idx: number) => (
                          <tr key={idx} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                            <td className="py-2.5 px-1">
                              <span className="font-mono font-semibold text-primary text-xs bg-primary/8 px-2 py-0.5 rounded">{cpt.code}</span>
                            </td>
                            <td className="py-2.5 px-1 text-slate-600 dark:text-slate-300 text-xs max-w-[180px] truncate">{cpt.description}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </SectionCard>
              )}
            </div>
          )}

          {/* ── Bundle Tab ── */}
          {activeTab === 'bundle' && bundle && (
            <div className="space-y-5">
              <SectionCard title="Bundle Overview" icon={Package}>
                <InfoRow icon={<Package className="w-4 h-4" />} label="Name" value={bundle.name} />
                {bundle.description && <InfoRow icon={<FileText className="w-4 h-4" />} label="Description" value={bundle.description} />}
                {bundle.category && <InfoRow icon={<Tag className="w-4 h-4" />} label="Category" value={
                  typeof bundle.category === 'object' && bundle.category !== null
                    ? ((bundle.category as any)?.name || (bundle.category as any)?.code || "")
                    : String(bundle.category || "")
                } />}
                <InfoRow icon={<Tag className="w-4 h-4" />} label="Status" value={
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${bundle.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                    {bundle.status || 'active'}
                  </span>
                } />
              </SectionCard>

              {(bundle.totalPrice != null || bundle.finalPrice != null) && (
                <SectionCard title="Pricing" icon={DollarSign}>
                  {bundle.totalPrice != null && <InfoRow icon={<DollarSign className="w-4 h-4" />} label="Total Price" value={`$${Number(bundle.totalPrice).toLocaleString()}`} />}
                  {bundle.discountPercent != null && <InfoRow icon={<Tag className="w-4 h-4" />} label="Discount" value={`${bundle.discountPercent}%`} />}
                  {bundle.finalPrice != null && (
                    <div className="mt-2 pt-2 border-t border-slate-100 dark:border-slate-700">
                      <InfoRow icon={<DollarSign className="w-4 h-4 text-emerald-600" />} label="Final Price" value={
                        <span className="text-lg font-bold text-emerald-600">${Number(bundle.finalPrice).toLocaleString()}</span>
                      } />
                    </div>
                  )}
                </SectionCard>
              )}

              {bundle.cptCodes && bundle.cptCodes.length > 0 && (
                <SectionCard title={`Included Services (${bundle.cptCodes.length})`} icon={Hash}>
                  <div className="overflow-x-auto -mx-1">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-100 dark:border-slate-700">
                          <th className="text-left py-2 px-1 text-xs font-semibold text-slate-500 uppercase tracking-wide">Code</th>
                          <th className="text-left py-2 px-1 text-xs font-semibold text-slate-500 uppercase tracking-wide">Description</th>
                          <th className="text-right py-2 px-1 text-xs font-semibold text-slate-500 uppercase tracking-wide">Price</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-50 dark:divide-slate-800">
                        {bundle.cptCodes.map((cpt: any) => (
                          <tr key={cpt.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                            <td className="py-2.5 px-1">
                              <span className="font-mono font-semibold text-primary text-xs bg-primary/8 px-2 py-0.5 rounded">{cpt.code}</span>
                            </td>
                            <td className="py-2.5 px-1 text-slate-600 dark:text-slate-300 text-xs max-w-[200px] truncate">{cpt.description}</td>
                            <td className="py-2.5 px-1 text-right font-semibold text-slate-700 dark:text-slate-300 text-xs">${Number(cpt.price || 0).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </SectionCard>
              )}

              {bundle.customItems && Array.isArray(bundle.customItems) && bundle.customItems.length > 0 && (
                <SectionCard title={`Additional Items / Extra Services (${bundle.customItems.length})`} icon={Package}>
                  <div className="overflow-x-auto -mx-1">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-100 dark:border-slate-700">
                          <th className="text-left py-2 px-1 text-xs font-semibold text-slate-500 uppercase tracking-wide">Item Name</th>
                          <th className="text-left py-2 px-1 text-xs font-semibold text-slate-500 uppercase tracking-wide">Description</th>
                          <th className="text-right py-2 px-1 text-xs font-semibold text-slate-500 uppercase tracking-wide">Price</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-50 dark:divide-slate-800">
                        {bundle.customItems.map((item: any, i: number) => (
                          <tr key={item.id || i} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                            <td className="py-2.5 px-1 text-slate-800 dark:text-slate-200 font-medium text-xs">{item.name}</td>
                            <td className="py-2.5 px-1 text-slate-500 dark:text-slate-400 text-xs max-w-[200px] truncate">{item.description || '-'}</td>
                            <td className="py-2.5 px-1 text-right font-semibold text-slate-900 dark:text-white text-xs">${Number(item.price || 0).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </SectionCard>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {apt.status === 'PENDING' && (
          <div className="px-7 py-5 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900">
            <Button
              variant="destructive"
              className="w-full"
              onClick={() => { onCancel(apt.id); onClose(); }}
              disabled={isCancelling}
            >
              <X className="w-4 h-4 mr-2" /> Cancel Request
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Appointment Card ─────────────────────────────────────────────────────────
function AppointmentCard({ apt, estimatesData, onView, onCancel, isCancelling }: {
  apt: any;
  estimatesData?: any[];
  onView: (apt: any) => void;
  onCancel: (id: string) => void;
  isCancelling: boolean;
}) {
  const pu = apt.groupUser || apt.provider?.user;
  const providerName = apt.groupName || pu?.displayName || `${pu?.firstName || ''} ${pu?.lastName || ''}`.trim() || 'Provider';
  const providerSlug = providerName.toLowerCase().replace(/\s+/g, '-');
  const serviceName = apt.bundle?.name || (apt.notes?.startsWith('[ESTIMATE:') ? apt.notes.match(/\[ESTIMATE:([^:\]]+)/)?.[1] : 'General Visit');
  const cleanNotes = apt.notes ? apt.notes.replace(/\[ESTIMATE:[^\]]+\]/g, '').trim() : '';

  let cptCodes: any[] = [];
  if (apt.bundle?.cptCodes && Array.isArray(apt.bundle.cptCodes) && apt.bundle.cptCodes.length > 0) {
    cptCodes = apt.bundle.cptCodes;
  } else if (estimatesData) {
    const estId = apt.notes?.match(/\[ESTIMATE:[^:]+:([^\]]+)\]/)?.[1];
    if (estId) {
      const est = estimatesData.find((e: any) => String(e.id) === String(estId));
      if (est?.items && Array.isArray(est.items)) {
        cptCodes = est.items;
      }
    }
  }

  const isGroup = apt.groupName || 
                  apt.provider?.practiceType?.toLowerCase() === 'group' || 
                  apt.provider?.practiceType?.toLowerCase() === 'clinic';

  return (
    <div className="bg-white dark:bg-slate-900 rounded-2xl sm:rounded-3xl border border-slate-200/80 dark:border-slate-800 p-4 sm:p-5 lg:p-6 shadow-xs hover:shadow-md hover:border-primary/40 transition-all flex flex-col justify-between gap-4 sm:gap-5">
      {/* Top Header Row: Provider Avatar, Name, Service Title & Status */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 sm:gap-4">
        <div className="flex items-start gap-3 sm:gap-3.5 min-w-0 flex-1">
          <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0 mt-0.5">
            {isGroup ? <Building2 className="w-5 h-5 text-primary" /> : <Stethoscope className="w-5 h-5 text-primary" />}
          </div>
          <div className="space-y-0.5 min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <a
                href={`/provider/${providerSlug}/${apt.provider?.userId || pu?.id}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <h3 className="text-sm sm:text-base font-bold text-slate-900 dark:text-white hover:text-primary hover:underline cursor-pointer transition-colors leading-snug">
                  {providerName}
                </h3>
              </a>
              {apt.provider?.specialty && (
                <span className="text-[11px] sm:text-xs text-slate-400 dark:text-slate-500 font-medium hidden sm:inline">
                  • {apt.provider.specialty}
                </span>
              )}
            </div>
            <p className="text-xs sm:text-sm font-extrabold text-slate-800 dark:text-slate-200 leading-snug">
              {serviceName}
            </p>
          </div>
        </div>

        <div className="self-start sm:self-auto shrink-0">
          <Badge variant="outline" className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${getStatusColor(apt.status)}`}>
            {getStatusLabel(apt.status)}
          </Badge>
        </div>
      </div>

      {/* Middle Information Container Boxes (Structured Grid for Laptop & Bigger Screens) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5 sm:gap-3 pt-0.5">
        {/* Box 1: Requested Date & Time */}
        <div className="flex items-center gap-2.5 p-3 rounded-xl bg-slate-50/90 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800 min-w-0">
          <div className="w-7 h-7 rounded-lg bg-blue-50 dark:bg-blue-950/60 text-primary flex items-center justify-center shrink-0">
            <Clock className="w-3.5 h-3.5" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider truncate">
              Requested On
            </p>
            <p className="text-xs font-semibold text-slate-800 dark:text-slate-200 truncate mt-0.5">
              {format(new Date(apt.createdAt), 'MMM d, yyyy · h:mm a')}
            </p>
          </div>
        </div>

        {/* Box 2: Verification Code Voucher (if applicable) */}
        {apt.verificationCode && apt.status !== 'COMPLETED' && apt.status !== 'CANCELLED' && apt.status !== 'REJECTED' ? (
          <div className="flex items-center gap-2.5 p-3 rounded-xl bg-indigo-50/80 dark:bg-indigo-950/40 border border-indigo-100 dark:border-indigo-900/60 min-w-0">
            <div className="w-7 h-7 rounded-lg bg-indigo-100 dark:bg-indigo-900/60 text-indigo-600 dark:text-indigo-400 flex items-center justify-center shrink-0">
              <Key className="w-3.5 h-3.5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-bold text-indigo-500 dark:text-indigo-400 uppercase tracking-wider truncate">
                Voucher Code
              </p>
              <p className="font-mono text-xs font-black text-indigo-700 dark:text-indigo-300 tracking-wider truncate mt-0.5">
                {apt.verificationCode}
              </p>
            </div>
          </div>
        ) : null}

        {/* Box 3: Included CPT Codes */}
        {cptCodes && cptCodes.length > 0 ? (
          <div className="flex items-start gap-2.5 p-3 rounded-xl bg-slate-50/90 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800 min-w-0 sm:col-span-2 lg:col-span-1">
            <div className="w-7 h-7 rounded-lg bg-emerald-50 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0 mt-0.5">
              <Tag className="w-3.5 h-3.5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-1">
                Included CPT ({cptCodes.length})
              </p>
              <div className="flex items-center gap-1.5 flex-wrap">
                {cptCodes.slice(0, 3).map((c: any, idx: number) => {
                  const codeStr = c.code || c.cptCode || (typeof c === 'string' ? c : '');
                  if (!codeStr) return null;
                  return (
                    <Badge key={c.id || codeStr || idx} variant="outline" className="font-mono text-[10px] bg-primary/10 text-primary border-primary/20 px-1.5 py-0.5 font-bold">
                      {codeStr}
                    </Badge>
                  );
                })}
                {cptCodes.length > 3 && (
                  <span className="text-[10px] text-primary font-bold">
                    +{cptCodes.length - 3} more
                  </span>
                )}
              </div>
            </div>
          </div>
        ) : null}

        {/* Box 4: Notes (if present) */}
        {cleanNotes ? (
          <div className="flex items-start gap-2.5 p-3 rounded-xl bg-slate-50/90 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-800 min-w-0 sm:col-span-2 lg:col-span-3">
            <div className="w-7 h-7 rounded-lg bg-amber-50 dark:bg-amber-950/60 text-amber-600 dark:text-amber-400 flex items-center justify-center shrink-0 mt-0.5">
              <FileText className="w-3.5 h-3.5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-0.5">
                Notes for Provider
              </p>
              <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed line-clamp-2">
                {cleanNotes}
              </p>
            </div>
          </div>
        ) : null}
      </div>

      {/* Bottom Pricing & Actions Bar */}
      <div className="pt-3.5 border-t border-slate-100 dark:border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="text-xs text-slate-500 dark:text-slate-400 font-medium">
            Estimated Amount:
          </span>
          <span className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white tracking-tight">
            ${Number(apt.amount || 0).toLocaleString()}
          </span>
          <span className="text-[10px] text-slate-400 dark:text-slate-500 font-semibold uppercase tracking-wider ml-1">
            All-inclusive rate
          </span>
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          <Button
            variant="outline"
            size="sm"
            className="text-primary border-primary/30 hover:bg-primary/5 hover:border-primary/60 gap-1.5 flex-1 sm:flex-initial rounded-xl font-bold text-xs h-9 px-3.5 cursor-pointer"
            onClick={() => onView(apt)}
          >
            <Eye className="w-3.5 h-3.5" /> View Details
          </Button>

          {apt.status === 'PENDING' && (
            <Button
              variant="destructive"
              size="sm"
              className="h-9 px-3.5 text-xs rounded-xl font-bold flex-1 sm:flex-initial cursor-pointer"
              onClick={() => onCancel(apt.id)}
              disabled={isCancelling}
            >
              Cancel Request
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export function MyAppointments() {
  const { toast } = useToast();
  const updateStatus = useUpdateAppointmentStatus();
  const [viewApt, setViewApt] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<"upcoming" | "past">("upcoming");
  const [dummyAppointments, setDummyAppointments] = useState<any[]>(DUMMY_APPOINTMENTS);

  const handleCancel = async (id: string) => {
    try {
      await updateStatus.mutateAsync({ id, status: 'CANCELLED' });
      toast({ title: 'Appointment Cancelled', description: 'Your appointment request has been cancelled.' });
    } catch {
      // Local preview fallback for dummy appointments
      setDummyAppointments((prev) =>
        prev.map((a) => (a.id === id ? { ...a, status: 'CANCELLED' } : a))
      );
      toast({ title: 'Appointment Cancelled', description: 'Your appointment request has been cancelled.' });
    }
  };

  const [search, setSearch] = useState(() => {
    if (typeof window !== 'undefined') {
      return new URLSearchParams(window.location.search).get('search') || "";
    }
    return "";
  });
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [quickDate, setQuickDate] = useState("all");

  const handleDateRangeSelect = (val: string) => {
    setQuickDate(val);
    const now = new Date();
    let start = new Date();
    let end = new Date();

    if (val === 'all') { setStartDate(''); setEndDate(''); setPage(1); return; }

    switch (val) {
      case 'yesterday':   start.setDate(start.getDate() - 1); end.setDate(end.getDate() - 1); break;
      case 'this-week':   start.setDate(start.getDate() - start.getDay()); end.setDate(start.getDate() + 6); break;
      case 'last-week':   start.setDate(start.getDate() - start.getDay() - 7); end = new Date(start); end.setDate(start.getDate() + 6); break;
      case 'this-month':  start.setDate(1); end = new Date(now.getFullYear(), now.getMonth() + 1, 0); break;
      case 'last-month':  start = new Date(now.getFullYear(), now.getMonth() - 1, 1); end = new Date(now.getFullYear(), now.getMonth(), 0); break;
      case 'this-year':   start = new Date(now.getFullYear(), 0, 1); end = new Date(now.getFullYear(), 11, 31); break;
    }

    const fmt = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    setStartDate(fmt(start)); setEndDate(fmt(end)); setPage(1);
  };

  const { data: appointmentsData, isLoading, error } = useGetMemberAppointments({ search, startDate, endDate, page: 1, limit: 100 });
  const { data: estimatesData } = useListMemberEstimates();

  const combinedAppointments = useMemo(() => {
    let list = [...(appointmentsData?.items || [])];

    if (list.length === 0) {
      list = [...dummyAppointments];
    }

    if (estimatesData && Array.isArray(estimatesData)) {
      estimatesData.forEach((est: any) => {
        const s = String(est.status || '').toUpperCase();
        const itemsObj = (est.items as any) || {};
        const paymentDetails = itemsObj.paymentDetails || {};
        const noteStr = String(est.negotiationNote || (Array.isArray(itemsObj.history) ? itemsObj.history[itemsObj.history.length - 1]?.note : '') || '');

        if (s === 'COMPLETED' || s === 'PAID' || paymentDetails.redemptionCode) {
          const estIdStr = String(est.id);
          const alreadyInList = list.some((a: any) => String(a.id) === estIdStr || (a.notes && String(a.notes).includes(estIdStr)));

          if (!alreadyInList) {
            list.push({
              id: `est-apt-${est.id}`,
              status: paymentDetails.redemptionStatus === 'REDEEMED' ? 'COMPLETED' : (s === 'REFUNDED' ? 'CANCELLED' : (s === 'COMPLETED' || s === 'PAID' ? 'APPROVED' : s)),
              amount: Number(est.finalAmount || est.totalCharges || 0),
              createdAt: est.updatedAt || est.createdAt || new Date().toISOString(),
              notes: `[ESTIMATE:${est.name || 'Paid Estimate'}:${est.id}] ${noteStr}`,
              provider: est.provider,
              verificationCode: paymentDetails.redemptionCode,
              bundle: est.bundle || null
            });
          }
        }
      });
    }

    if (search) {
      const q = search.toLowerCase().trim();
      return list.filter((apt: any) => {
        const pName = (apt.groupName || apt.provider?.displayName || `${apt.provider?.firstName || ''} ${apt.provider?.lastName || ''}`).toLowerCase();
        const title = (apt.bundle?.name || apt.notes || '').toLowerCase();
        return pName.includes(q) || title.includes(q);
      });
    }

    return list;
  }, [appointmentsData, dummyAppointments, estimatesData, search]);

  const upcomingAppointments = useMemo(() => {
    return combinedAppointments.filter((apt: any) => {
      const s = String(apt.status || '').toUpperCase();
      return s === 'PENDING' || s === 'APPROVED' || s === 'ACCEPTED' || s === 'CONFIRMED' || s === 'SCHEDULED' || s === 'PAID';
    });
  }, [combinedAppointments]);

  const pastAppointments = useMemo(() => {
    return combinedAppointments.filter((apt: any) => {
      const s = String(apt.status || '').toUpperCase();
      return s === 'COMPLETED' || s === 'REDEEMED' || s === 'CANCELLED' || s === 'REJECTED' || s === 'REFUNDED';
    });
  }, [combinedAppointments]);

  const currentDisplayList = activeTab === "upcoming" ? upcomingAppointments : pastAppointments;
  const displayTotalPages = Math.ceil(currentDisplayList.length / limit) || 1;
  const paginatedDisplayList = useMemo(() => {
    return currentDisplayList.slice((page - 1) * limit, page * limit);
  }, [currentDisplayList, page, limit]);

  return (
    <MemberLayout>
      <div className="w-full space-y-6 sm:space-y-7 pt-4 sm:pt-6 lg:pt-8 pb-16 flex flex-col min-h-[calc(100vh-10rem)]">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-5 border-b border-slate-200/80 dark:border-slate-800">
          <div className="flex items-center gap-3.5 sm:gap-4">
            <div className="p-3 sm:p-3.5 bg-primary/10 text-primary rounded-2xl shrink-0">
              <CalendarIcon className="w-6 h-6 sm:w-7 sm:h-7 text-primary" />
            </div>
            <div className="space-y-0.5">
              <h1 className="text-xl sm:text-2xl font-extrabold tracking-tight text-slate-900 dark:text-white">Appointments</h1>
              <p className="text-slate-500 dark:text-slate-400 text-xs sm:text-sm font-medium">
                View and manage your upcoming bookings, paid appointment vouchers, and past appointment history.
              </p>
            </div>
          </div>
        </div>

        {/* Search & Filter Toolbar */}
        <div className="flex flex-col sm:flex-row gap-3 items-center">
          <div className="relative flex-1 w-full">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 shrink-0 pointer-events-none" />
            <Input
              placeholder="Search by provider or service name..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="pl-10 pr-9 h-10 sm:h-11 rounded-xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-xs sm:text-sm"
            />
            {search && (
              <button
                type="button"
                onClick={() => { setSearch(""); setPage(1); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors cursor-pointer"
                title="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="w-full sm:w-auto">
            <Select value={quickDate} onValueChange={handleDateRangeSelect}>
              <SelectTrigger className="h-10 sm:h-11 w-full sm:w-44 rounded-xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-xs font-semibold cursor-pointer">
                <SelectValue placeholder="Select range" />
              </SelectTrigger>
              <SelectContent className="rounded-xl z-50">
                <SelectItem value="all">All Time</SelectItem>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="yesterday">Yesterday</SelectItem>
                <SelectItem value="this-week">This Week</SelectItem>
                <SelectItem value="last-week">Last Week</SelectItem>
                <SelectItem value="this-month">This Month</SelectItem>
                <SelectItem value="last-month">Last Month</SelectItem>
                <SelectItem value="this-year">This Year</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Upcoming vs Past Tabs */}
        <div className="flex gap-2 border-b border-slate-200 dark:border-slate-800 pb-1 overflow-x-auto">
          <button
            type="button"
            onClick={() => { setActiveTab("upcoming"); setPage(1); }}
            className={`px-4 py-2 text-xs sm:text-sm font-bold rounded-xl transition-all flex items-center gap-2 cursor-pointer shrink-0 ${
              activeTab === "upcoming"
                ? "bg-primary text-white shadow-xs"
                : "text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
            }`}
          >
            <Clock className="w-4 h-4 shrink-0" /> Upcoming Appointments ({upcomingAppointments.length})
          </button>
          <button
            type="button"
            onClick={() => { setActiveTab("past"); setPage(1); }}
            className={`px-4 py-2 text-xs sm:text-sm font-bold rounded-xl transition-all flex items-center gap-2 cursor-pointer shrink-0 ${
              activeTab === "past"
                ? "bg-primary text-white shadow-xs"
                : "text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
            }`}
          >
            <History className="w-4 h-4 shrink-0" /> Past Appointments History ({pastAppointments.length})
          </button>
        </div>

        {/* List */}
        <div className="flex-1 pb-10 mt-4">
          {isLoading ? (
            <div className="p-8 text-center text-slate-500">Loading appointments...</div>
          ) : error ? (
            <div className="p-8 text-center text-red-500">Failed to load appointments.</div>
          ) : currentDisplayList.length === 0 ? (
            <div className="bg-white dark:bg-slate-900 rounded-3xl border border-slate-200 dark:border-slate-800 p-12 text-center shadow-sm">
              <CalendarIcon className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
                No {activeTab === "upcoming" ? "upcoming" : "past"} appointments found
              </h3>
              <p className="text-slate-500 dark:text-slate-400 max-w-md mx-auto text-sm">
                {search || startDate || endDate
                  ? "Try adjusting your search filters."
                  : activeTab === "upcoming"
                    ? "When you book or purchase healthcare service packages, your scheduled appointments will appear here."
                    : "Your completed, cancelled, or past appointment history will appear here once finalized."}
              </p>
            </div>
          ) : (
            <div className="space-y-5 sm:space-y-6 flex-1 flex flex-col justify-between min-h-[400px]">
              <div className="space-y-5 sm:space-y-6">
                {paginatedDisplayList.map((apt: any) => (
                  <AppointmentCard
                    key={apt.id}
                    apt={apt}
                    estimatesData={estimatesData}
                    onView={setViewApt}
                    onCancel={handleCancel}
                    isCancelling={updateStatus.isPending}
                  />
                ))}
              </div>

              {displayTotalPages > 1 && (
                <div className="mt-auto pt-6 border-t border-slate-200 dark:border-slate-800 flex justify-center flex-shrink-0">
                  <ListPagination currentPage={page} totalPages={displayTotalPages} onPageChange={setPage} totalCount={currentDisplayList.length} limit={limit} onLimitChange={setLimit} />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      {viewApt && (
        <AppointmentDetailModal
          apt={viewApt}
          onClose={() => setViewApt(null)}
          onCancel={handleCancel}
          isCancelling={updateStatus.isPending}
        />
      )}
    </MemberLayout>
  );
}
